import type { KeyboardEvent, ReactNode } from "react";

import { cn, hasRenderableNode } from "../base/utils";

export interface WorkbenchRankedListMetric {
  id: string;
  label: ReactNode;
  value: ReactNode;
}

export interface WorkbenchRankedListItem {
  id: string;
  label: ReactNode;
  description?: ReactNode;
  score?: ReactNode;
  tags?: ReactNode[];
  metrics?: WorkbenchRankedListMetric[];
  leading?: ReactNode;
  trailing?: ReactNode;
}

export interface WorkbenchRankedListProps {
  items: WorkbenchRankedListItem[];
  selectedItemId?: string;
  onItemSelect?: (item: WorkbenchRankedListItem) => void;
  emptyState?: ReactNode;
  ariaLabel?: string;
  className?: string;
}

const activateItem = (
  event: KeyboardEvent<HTMLButtonElement>,
  item: WorkbenchRankedListItem,
  onItemSelect?: (item: WorkbenchRankedListItem) => void,
) => {
  if (!onItemSelect || (event.key !== "Enter" && event.key !== " ")) return;
  event.preventDefault();
  onItemSelect(item);
};

export function WorkbenchRankedList({
  items,
  selectedItemId,
  onItemSelect,
  emptyState = null,
  ariaLabel = "Ranked items",
  className,
}: WorkbenchRankedListProps) {
  if (!items.length) {
    return hasRenderableNode(emptyState) ? <>{emptyState}</> : null;
  }

  return (
    <ol
      aria-label={ariaLabel}
      data-monkeys-component="workbench-ranked-list"
      className={cn("grid min-w-0 gap-2", className)}
    >
      {items.map((item, index) => {
        const selected = selectedItemId === item.id;
        return (
          <li key={item.id}>
            <button
              type="button"
              aria-pressed={onItemSelect ? selected : undefined}
              onClick={
                onItemSelect ? () => onItemSelect(item) : undefined
              }
              onKeyDown={(event) => activateItem(event, item, onItemSelect)}
              className={cn(
                "grid w-full min-w-0 grid-cols-[auto_minmax(0,1fr)_auto] items-start gap-3 rounded-lg border bg-card p-3 text-left text-card-foreground outline-none transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none",
                selected
                  ? "border-primary bg-primary/5"
                  : "border-border/70 hover:border-primary/40 hover:bg-accent/50",
                !onItemSelect && "cursor-default",
              )}
            >
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold tabular-nums text-muted-foreground">
                {item.leading || index + 1}
              </span>
              <span className="min-w-0">
                <span className="block truncate text-sm font-semibold text-foreground">
                  {item.label}
                </span>
                {hasRenderableNode(item.description) ? (
                  <span className="mt-1 block text-xs leading-5 text-muted-foreground">
                    {item.description}
                  </span>
                ) : null}
                {item.tags?.length ? (
                  <span className="mt-2 flex flex-wrap gap-1.5">
                    {item.tags.map((tag, tagIndex) => (
                      <span
                        key={`${item.id}:tag:${tagIndex}`}
                        className="rounded-full bg-secondary px-2 py-0.5 text-[0.625rem] font-medium text-secondary-foreground"
                      >
                        {tag}
                      </span>
                    ))}
                  </span>
                ) : null}
                {item.metrics?.length ? (
                  <span className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-4">
                    {item.metrics.map((metric) => (
                      <span key={metric.id} className="min-w-0">
                        <span className="block truncate text-[0.625rem] text-muted-foreground">
                          {metric.label}
                        </span>
                        <span className="block text-xs font-semibold tabular-nums text-foreground">
                          {metric.value}
                        </span>
                      </span>
                    ))}
                  </span>
                ) : null}
              </span>
              <span className="shrink-0 text-right">
                {hasRenderableNode(item.score) ? (
                  <span className="block text-lg font-semibold tabular-nums text-primary">
                    {item.score}
                  </span>
                ) : null}
                {hasRenderableNode(item.trailing) ? (
                  <span className="mt-1 block text-muted-foreground">
                    {item.trailing}
                  </span>
                ) : null}
              </span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}
